// TenantContext — exposes the active tenant identity to the React tree.
//
// Multi-tenancy refactor PR5. Reads the slug from the URL at boot via
// `getTenantSlug()` (window.location.hostname → slug); reads the
// canonical tenant_id from the backend `/me` endpoint after login.
//
// Three reasons to wrap the SPA in this provider:
//   1. api/client.ts reads `slug` to set X-Tenant-Slug on every request,
//      which the backend (PR4 RequestContextMiddleware) verifies against
//      the Host-derived slug as a deploy-misconfig check.
//   2. UI chrome (tenant name in the navbar, branding) can render
//      synchronously from `slug` instead of waiting for /me.
//   3. Custom-domain SLA-tier customers (acme-hvac.com) have no slug in
//      the URL — `isCustomDomain` is true and the provider falls back
//      to /me for `tenantId`. The backend resolves the tenant from the
//      Host header (custom_domain column lookup), so the frontend
//      doesn't need to send a header — sending one would be wrong.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getTenantSlug, isCustomDomain as detectCustomDomain } from '../lib/tenantMode';

export interface TenantState {
  /** Tenant slug parsed from `<slug>.app.goeswestservices.com`. Null on
   *  custom-domain hosts (backend resolves via custom_domain) and on
   *  dev hosts where REACT_APP_TENANT_SLUG is not set. */
  slug: string | null;

  /** Canonical tenant_id from the backend, set after the first /me call.
   *  Null until that resolves (or until login completes). */
  tenantId: number | null;

  /** True when the SPA was served at a non-platform host (e.g.,
   *  acme-hvac.com). The frontend will NOT send X-Tenant-Slug in this
   *  case — the backend identifies the tenant from the Host header. */
  isCustomDomain: boolean;

  /** Imperative setter for `tenantId` — call from the login flow once
   *  /me responds. Kept narrow so the rest of the app never mutates
   *  tenant identity directly. */
  setTenantId: (id: number | null) => void;
}

const TenantContext = createContext<TenantState | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  // slug + isCustomDomain are derived from window.location at boot and
  // never change for the lifetime of the SPA (no SPA navigation can
  // cross a tenant boundary — that would require a full reload to a
  // different host).
  const [slug] = useState<string | null>(() => getTenantSlug());
  const [isCustomDomain] = useState<boolean>(() => detectCustomDomain());
  const [tenantId, setTenantId] = useState<number | null>(null);

  // Listen for the same `gws:logout` event the api client emits on 401;
  // clear tenantId so a fresh login resolves it again from /me.
  useEffect(() => {
    const onLogout = () => setTenantId(null);
    window.addEventListener('gws:logout', onLogout);
    return () => window.removeEventListener('gws:logout', onLogout);
  }, []);

  const value = useMemo<TenantState>(
    () => ({ slug, tenantId, isCustomDomain, setTenantId }),
    [slug, tenantId, isCustomDomain],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/**
 * Read the tenant state from any component. Throws if called outside
 * `<TenantProvider>` — that's a wiring bug, not a runtime fallback case.
 */
export function useTenant(): TenantState {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be called inside <TenantProvider>');
  }
  return ctx;
}

/**
 * Module-level getter for code that runs OUTSIDE the React tree
 * (api/client.ts is the primary caller — it needs to read the slug
 * before any hook can fire). Returns the same value the provider
 * sees at boot. Kept as a function so test code can monkeypatch.
 */
export function readTenantSlugFromUrl(): string | null {
  return getTenantSlug();
}
